import { useState } from 'react';
import type { NumberConfig, NumberValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';
import { FootnoteNote } from './FootnoteNote';

interface NumberInputProps {
  field: Pick<Field, 'label' | 'allowFootnote'>;
  config: NumberConfig;
  value: NumberValue | null;
  onCommit: (value: NumberValue | null) => void;
  disabled?: boolean;
}

export function NumberInput({ field, config, value, onCommit, disabled = false }: NumberInputProps) {
  const [text, setText] = useState(value?.value ?? '');
  const [note, setNote] = useState(value?.note ?? '');

  function commit() {
    if (disabled) {
      return;
    }
    if (text.trim() === '') {
      onCommit(null);
      return;
    }
    onCommit({ type: 'NUMBER', value: text.trim(), footnote: value?.footnote ?? false, note: value?.note ?? null });
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm text-text-muted">
        <span className="eyebrow">{field.label}</span>
        <input
          type="number"
          value={text}
          disabled={disabled}
          {...(config.min !== null ? { min: config.min } : {})}
          {...(config.max !== null ? { max: config.max } : {})}
          {...(config.step !== null ? { step: config.step } : {})}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
          className="telemetry rounded-md border border-border-strong bg-bg px-3 py-2 text-text focus:border-accent"
        />
      </label>
      {field.allowFootnote && (
        <FootnoteNote
          footnote={value?.footnote ?? false}
          note={note}
          disabled={disabled || !value}
          onFootnoteChange={(footnote) => value && onCommit({ ...value, footnote })}
          onNoteChange={setNote}
          onNoteBlur={() => value && onCommit({ ...value, note: note.trim() === '' ? null : note })}
        />
      )}
    </div>
  );
}
